/**
 * Product gallery.
 *
 * Everything here is an upgrade on markup that already works: the thumbnails
 * are real `<a href="#slide">` links, the track is a native scroll container,
 * and the dots are decorative. With no JavaScript a customer still reaches
 * every medium.
 *
 * What the script adds:
 *   - the current thumbnail and dot follow the track as it scrolls
 *   - a thumbnail scrolls the track rather than jumping the whole page
 *   - pointer-following origin for the desktop hover zoom
 */
(function () {
  'use strict';

  if (!window.Theme) return;

  class ProductGallery extends window.Theme.ThemeElement {
    init() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;

      this.slides = this.$$('[data-gallery-slide]');
      this.thumbs = this.$$('[data-gallery-thumb]');
      this.dots = this.$$('[data-gallery-dot]');

      if (this.slides.length > 1) {
        this.watchSlides();
        this.thumbs.forEach((thumb) => {
          this.on(thumb, 'click', (event) => this.onThumbClick(event, thumb));
        });
      }

      // Spotlight hides every slide that is not current, and on desktop nothing
      // is current until something marks it — the IntersectionObserver cannot,
      // because a `display: none` slide never intersects. Without this the
      // gallery rendered completely empty until a thumbnail was clicked.
      this.current = -1;
      this.setCurrent(0);

      this.wireZoom();
      this.jumpToInitialMedia();
    }

    /**
     * A variant with its own image opens on that image. No animation: this is
     * the starting state of a freshly rendered gallery, not a transition the
     * customer asked for.
     */
    jumpToInitialMedia() {
      const id = this.dataset.initialMedia;
      if (!id) return;
      const slide = this.querySelector('[data-media-id="' + id + '"]');
      if (!slide || slide.dataset.index === '0') return;

      if (this.track.scrollWidth > this.track.clientWidth) {
        this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'auto' });
      }
      this.setCurrent(Number(slide.dataset.index));
    }

    /**
     * Which slide is showing drives the indicators. An observer rather than a
     * scroll listener, so nothing runs while the gallery is off screen — and on
     * desktop, where the slides are a grid rather than a carousel, it keeps the
     * rail in step with the page scroll for free.
     */
    watchSlides() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.setCurrent(Number(entry.target.dataset.index));
          });
        },
        { root: null, threshold: 0.6 }
      );
      this.slides.forEach((slide) => this.observer.observe(slide));
    }

    setCurrent(index) {
      if (index === this.current) return;
      this.current = index;

      this.thumbs.forEach((thumb) => {
        const isCurrent = Number(thumb.dataset.index) === index;
        thumb.classList.toggle('is-current', isCurrent);
        if (isCurrent) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });

      this.dots.forEach((dot) => {
        dot.classList.toggle('is-current', Number(dot.dataset.index) === index);
      });

      // Spotlight shows exactly one slide, so the slides need the marker too.
      this.slides.forEach((slide) => {
        slide.classList.toggle('is-current', Number(slide.dataset.index) === index);
      });
    }

    /**
     * Scroll the track, not the document. The href stays in the markup as the
     * no-JavaScript path, but following it would jump the whole page down to
     * the slide and leave the buy column off screen.
     */
    onThumbClick(event, thumb) {
      const slide = this.slides[Number(thumb.dataset.index)];
      if (!slide) return;
      event.preventDefault();

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const horizontal = this.track.scrollWidth > this.track.clientWidth;
      const spotlight = this.dataset.layout === 'spotlight';

      // Spotlight swaps the visible photo, so there is nothing to scroll to —
      // and scrolling the page would move the buy column for no reason.
      if (spotlight && !horizontal) {
        this.setCurrent(Number(thumb.dataset.index));
        return;
      }

      if (horizontal) {
        this.track.scrollTo({
          left: slide.offsetLeft - this.track.offsetLeft,
          behavior: reduced ? 'auto' : 'smooth',
        });
      } else {
        // Desktop: the slides are a grid in the page flow.
        slide.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'nearest' });
      }

      this.setCurrent(Number(thumb.dataset.index));
    }

    /**
     * The zoom itself is CSS, gated on a fine pointer. This only tells it where
     * the pointer is, so the image magnifies about the spot being looked at
     * rather than about its centre.
     */
    wireZoom() {
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

      // Delegated from the track rather than bound to the lead slide: in
      // spotlight the photo being shown changes, and zoom has to follow it.
      this.on(this.track, 'pointermove', (event) => {
        const slide = event.target.closest('.gallery__slide');
        if (!slide) return;
        const box = slide.getBoundingClientRect();
        slide.style.setProperty('--zoom-x', ((event.clientX - box.left) / box.width) * 100 + '%');
        slide.style.setProperty('--zoom-y', ((event.clientY - box.top) / box.height) * 100 + '%');
      });

      this.on(this.track, 'pointerout', (event) => {
        const slide = event.target.closest('.gallery__slide');
        if (!slide || slide.contains(event.relatedTarget)) return;
        slide.style.removeProperty('--zoom-x');
        slide.style.removeProperty('--zoom-y');
      });
    }

    destroy() {
      if (this.observer) this.observer.disconnect();
    }
  }

  if (!customElements.get('product-gallery')) {
    customElements.define('product-gallery', ProductGallery);
  }
})();
